import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { apiFetch, getToken } from "@/api";

const MOOD_LABELS = {
  1: "Not great",
  2: "Could be better",
  3: "It's okay",
  4: "Pretty good!",
  5: "Loved it!",
};

export const ReviewPrompt = ({ onClose }) => {
  const navigate = useNavigate();
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [thanks, setThanks] = useState(false);

  const submit = async () => {
    if (!rating || submitting || thanks) return;
    if (!getToken()) {
      toast.error("Please log in to share your review.");
      navigate("/login");
      return;
    }
    setSubmitting(true);
    try {
      await apiFetch("/reviews", {
        method: "POST",
        body: { rating, reviewText: reviewText.trim() || undefined },
      });
      setThanks(true);
      setTimeout(onClose, 1500);
    } catch {
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const maybeLater = () => {
    apiFetch("/reviews/dismiss", { method: "POST" }).catch(() => {});
    onClose();
  };

  return (
    <div
      data-testid="review-prompt-card"
      className="rounded-[2rem] border border-[#E3ECE6] bg-white p-6 text-center shadow-sm"
    >
      <img
        data-testid="review-logo"
        src="/logo.png"
        alt="Abhyas"
        className="mx-auto h-8 w-auto"
      />
      <h2
        data-testid="review-heading"
        className="mt-3 font-display text-lg font-extrabold text-[#17201A]"
      >
        How's your experience with Abhyas?
      </h2>
      <p className="mt-1 text-xs font-semibold text-[#647067]">
        Your feedback helps us improve the whole app.
      </p>

      <div className="mt-3 flex items-center justify-center gap-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <button
            key={i}
            data-testid={`review-star-${i}`}
            onClick={() => setRating(i)}
            aria-label={`Rate ${i} star${i > 1 ? "s" : ""}`}
            className="rounded-full p-0.5 transition-transform duration-150 hover:scale-110"
          >
            <Star
              key={rating >= i ? `on-${rating}` : "off"}
              className={`h-8 w-8 transition-colors duration-150 ${
                rating >= i
                  ? "animate-star-pop fill-[#FACC15] text-[#FACC15]"
                  : "fill-[#E5EFE5] text-[#E5EFE5]"
              }`}
            />
          </button>
        ))}
      </div>

      <div
        className={`grid transition-[grid-template-rows,opacity] duration-300 ${
          rating > 0 ? "opacity-100 [grid-template-rows:1fr]" : "opacity-0 [grid-template-rows:0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <p
            data-testid="review-mood"
            className="pt-1 text-sm font-extrabold text-[#17201A]"
          >
            {rating > 0 ? MOOD_LABELS[rating] : ""}
          </p>
        </div>
      </div>

      <div
        className={`grid transition-[grid-template-rows,opacity] duration-300 ${
          rating > 0 ? "opacity-100 [grid-template-rows:1fr]" : "opacity-0 [grid-template-rows:0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="pt-3 text-left">
            <label
              data-testid="review-text-label"
              htmlFor="review-text-input"
              className="text-xs font-bold text-[#647067]"
            >
              Write a review (optional)
            </label>
            <textarea
              id="review-text-input"
              data-testid="review-text-input"
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="What did you like? What can be better?"
              className="mt-1.5 w-full resize-none rounded-2xl border border-[#E3ECE6] bg-[#F8FAF7] px-4 py-3 text-sm font-semibold text-[#17201A] outline-none transition-colors duration-200 placeholder:text-[#94A39B] focus:border-[#16A34A]"
            />
          </div>
        </div>
      </div>

      <div className="mt-4 grid gap-2.5">
        <button
          data-testid="review-submit"
          onClick={submit}
          disabled={!rating || submitting || thanks}
          className={`w-full rounded-full py-3.5 text-sm font-bold transition-[background-color,transform] duration-200 ${
            thanks
              ? "bg-[#16A34A]/15 text-[#16A34A]"
              : "bg-[#16A34A] text-white hover:bg-[#15803D] disabled:opacity-40"
          }`}
        >
          {thanks ? "Thanks!" : submitting ? "Submitting..." : "Submit"}
        </button>
        <button
          data-testid="review-later"
          onClick={maybeLater}
          className="w-full rounded-full border border-[#E3ECE6] py-3 text-sm font-bold text-[#647067] transition-colors duration-200 hover:text-[#17201A]"
        >
          Maybe later
        </button>
      </div>
    </div>
  );
};

export default ReviewPrompt;
